import { Checkbox, FilterOptionsState, createFilterOptions } from '@mui/material';
import React from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { CenteredCircularProgress } from 'components/Progress';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { ClearIcon } from '@mui/x-date-pickers';

import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';
import API from 'utils/api';

import Chip from 'components/Chip';

import { SearchResponse, UuidableName } from 'utils/api/types';

import { colors } from 'theme/colors';

import { StyledOption } from '../styles';
import { TagsAutocompleteProps } from './types';

const loadingOption = { uuid: 'loading', name: 'Loading...' } as SearchResponse;
type FilteringType = UuidableName | { inputValue: string; name: string } | null;
const filterOptions = createFilterOptions<FilteringType>();
const placeholder = 'Choose an option or type to add a new tag';

const TagsAutocomplete: React.FunctionComponent<TagsAutocompleteProps> = ({ tags, setFieldValue }) => {
  const {
    results: tagsSearchResults,
    searchQuery: tagsSearchQuery,
    loading: tagsLoading,
    loadingMore,
    hasMore,
    loadMore,
  } = useInfiniteSearch(API.devices.searchDeviceTags);

  return (
    <InfiniteAutocomplete
      loadingMore={loadingMore}
      hasMore={hasMore}
      onLoadMore={loadMore}
      multiple
      id="tag"
      options={tagsSearchResults}
      value={tags}
      disableCloseOnSelect
      renderOption={(renderOption, option, state) => {
        if (option === null) {
          return null;
        }
        if (option.uuid === loadingOption.uuid) {
          return (
            <StyledOption>
              <CenteredCircularProgress />
            </StyledOption>
          );
        }
        return (
          <StyledOption {...renderOption}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="medium" />}
              checkedIcon={<CheckBoxIcon fontSize="medium" />}
              checked={state.selected}
            />
            {option.name}
          </StyledOption>
        );
      }}
      getOptionLabel={(option) => {
        if (option === null) {
          return 'NULL';
        }
        if (typeof option === 'string') {
          return option;
        }
        // @ts-ignore
        if ('inputValue' in option) {
          return option.inputValue as string;
        }
        return option.name;
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          placeholder={placeholder}
          onChange={(e) => {
            tagsSearchQuery(e.target.value);
          }}
        />
      )}
      // @ts-ignore
      filterOptions={(options, params) => {
        const filtered = filterOptions(options, params as FilterOptionsState<FilteringType>);

        const { inputValue } = params;
        const exists = options.some((option) => option !== null && inputValue === option.name);
        if (inputValue !== '' && !exists) {
          filtered.push({
            inputValue,
            name: `Add "${inputValue}"`,
          });
        }

        return filtered;
      }}
      renderTags={(tagValue, getTagProps) => (
        <>
          {tagValue.map((option, index) =>
            option ? (
              <Chip
                {...getTagProps({ index })}
                label={`${option.name}`}
                variant="outlined"
                size="small"
                colors={{
                  background: colors.common.white,
                  border: colors.ui.chip.border,
                }}
                iconRight={<ClearIcon />}
                iconRightDisableInnerMargin
              />
            ) : null
          )}
        </>
      )}
      loading={tagsLoading}
      onChange={(_, value) => {
        const mappedTags = value.map((tag) => {
          // @ts-ignore
          if ('inputValue' in tag) {
            return {
              name: tag.inputValue,
              uuid: crypto.randomUUID(),
            };
          }
          return tag;
        });
        setFieldValue('tags', mappedTags);
        tagsSearchQuery('');
      }}
      isOptionEqualToValue={(option: SearchResponse | null, val: SearchResponse | null) =>
        val?.uuid !== undefined && val?.uuid === option?.uuid
      }
      fullWidth
      freeSolo
      forcePopupIcon
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
    />
  );
};

export default TagsAutocomplete;
